<template>
  <OfferTile
    :offer="offer"
    :card="customCard"
    :tag-title="tagTitle"
    link-tag="nuxt-link"
    :limit-color-variants="3"
    :lazy="lazy"
  >
    <template #image>
      <div
        v-if="!imageSources"
        class="offer-tile__image-404"
      />
    </template>
    <template #brand>
      <CdnImage
        v-if="brand?.logo"
        :class="['offer-tile__logo-image', `offer-tile__logo-image--${brand.slug}`]"
        render-without-figure
        :src="brand.logo"
        :alt="`${brand.name} Logo`"
        :image-style="`aspect-ratio: ${brand.aspectRatio}`"
        :lazy="lazy"
      />
      <span
        v-else-if="brand"
        class="offer-tile__brand-placeholder"
        >{{ brand.name }}</span
      >
    </template>

    <template
      v-if="telekomContractLink"
      #offerContract
    >
      <LinkButton
        class="offer-tile__contract-link link--external"
        tag="button"
        text
        @click.native.prevent="clickContractLink"
      >
        {{ contractLinkText }}
        <span class="sr-only">(Der Link wird in einem neuen Tab geöffnet)</span>
      </LinkButton>
    </template>
    <template #info>
      <div
        v-if="inStock"
        class="offer-tile__delivery"
        aria-hidden="true"
      >
        <span v-if="availability.deliveryTime">
          Lieferzeit: <span class="text text--nowrap">{{ availability.deliveryTime }}</span>
        </span>
      </div>
      <div
        v-else-if="availability"
        class="offer-tile__unavailable"
      >
        {{ availability.text }}
      </div>
    </template>
    <template #button>
      <div
        v-if="hasCartButton"
        class="offer-tile__add-to-cart"
      >
        <AddToCartButtonContainer
          display-cart-quantity
          :display-text="false"
          :offer="offer"
        />
      </div>
      <client-only>
        <LinkButton
          v-if="isBookmarkingAvailable"
          class="offer-tile__bookmark"
          :class="{ 'offer-tile__bookmark--active': isBookmarked }"
          tag="button"
          :outline="!isBookmarked"
          :outline-primary="isBookmarked"
          small-padding
          icon-only
          :aria-label="
            !isBookmarked
              ? 'Produkt zur Merkliste hinzufügen'
              : 'Produkt von der Merkliste entfernen'
          "
          @click.native.prevent="toggleBookmarkNumber"
        >
          <svg-icon
            no-icon-class
            class="offer-tile__bookmark-icon"
            :image="IconBookmark"
          />
        </LinkButton>
      </client-only>
    </template>
    <template
      v-if="isMzaCompatible || isMshCompatible"
      #icon
    >
      <div class="offer-tile__app-icons">
        <span
          v-if="isMzaCompatible"
          class="offer-tile__svg-icon-wrapper"
          title="MagentaZuhause App"
        >
          <SvgIcon
            :image="mzaIcon"
            class="offer-tile__mza-icon"
            aria-label="Kompatibel mit der MagentaZuhause App"
            :aria-hidden="false"
          />
        </span>
        <span
          v-if="isMshCompatible"
          class="offer-tile__svg-icon-wrapper"
          title="Magenta SmartHome App"
        >
          <SvgIcon
            :image="mshIcon"
            :aria-hidden="false"
            aria-label="Kompatibel mit der Magenta SmartHome App"
          />
        </span>
      </div>
    </template>
  </OfferTile>
</template>

<script setup lang="ts">
import { merge, cloneDeep } from 'lodash-es';
import { withDefaults, defineAsyncComponent, computed } from 'vue';
import { useRoute } from '#imports';
import { storeToRefs } from 'pinia';
import { useOfferStore } from '~/stores/offers';
import { useCouponStore } from '~/stores/coupon';
import { OfferTile, LinkButton, calculateAvailability } from '@i22/td-longtail-vue-components';
import { getDiscount, getPrice } from '@i22/rocket';

import CdnImage from '~/components/cdn-image.vue';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';

import { openModal } from '~/composables/modal';
import useProductBookmarks from '~/composables/product-bookmarks';

import { getBrand } from '~/lib/brand';
import { getMainImageFromOffer } from '~/lib/goliath-media';
import { getTelekomContractLink, getTelekomContractLinkText } from '~/lib/telekom-contract-link';
import type { ListOffer } from '~/lib/goliath/offers';

import IconBookmark from '~/assets/images/icon-bookmark.svg';

import mzaIcon from '~/assets/images/mza-logo-magenta-solid.svg';
import mshIcon from '~/assets/images/msh-logo.svg';

import type { CardComponentProps } from '@i22/td-longtail-vue-components/dist/components/card/card.vue';
import type { SquareBadgeComponentProps } from '@i22/td-longtail-vue-components/dist/components/square-badge.vue';
import { tealiumLink } from '~/lib/tealium';

const HIGHLIGHTS_OFFER = [
  'DCS8302LH-SPK',
  '4251281645534',
  '8718699787059',
  '4251281641420',
  '4251281645138',
];

const $route = useRoute();

export type OfferTileContainerProps = {
  tagTitle?: string;
  offer: ListOffer;
  hasCartButton?: boolean;
  lazy?: boolean;
  card?: CardComponentProps | undefined;
};

const props = withDefaults(defineProps<OfferTileContainerProps>(), {
  tagTitle: 'h2',
  card: undefined,
  lazy: false,
});

const {
  getCoupon,
  isValidCoupon,
  reductionValue,
  compatibleArticleNumbers,
  compatibleProductProviders,
  incompatibleArticleNumbers,
} = storeToRefs(useCouponStore());
const { zeroPercentFinancing, rebates } = storeToRefs(useOfferStore());

const { isProductInBookmarks, isBookmarkingAvailable, toggleBookmark } = useProductBookmarks();

const isBookmarked = computed(() => isProductInBookmarks(props.offer?.product?.articleNumber));

const availability = computed(() => calculateAvailability(props.offer.product?.availability, true));

const alt = computed(() => props.offer.product?.name);

const rebateDescription = computed(
  () => rebates.value[props.offer.product.articleNumber]?.description
);

const deliveryLeadTime = computed(() => props.offer.product?.deliveryLeadTime);

const imageSources = computed(() => getMainImageFromOffer(props.offer));

const brand = computed(() => getBrand(props.offer));

const productTags = computed(() => {
  if (!props.offer?.product) return [];
  let tags = [];
  if (props.offer.product.bundledProducts?.length > 0) {
    const { bundledProducts } = props.offer.product;
    tags = bundledProducts[0].tags?.filter((tag) =>
      bundledProducts.every((product) => product.tags?.includes(tag))
    );
  } else if (props.offer.product.tags) {
    tags = props.offer.product.tags;
  }
  return tags?.map((str) => str.toLowerCase()) || [];
});

const isMzaCompatible = computed(() =>
  ['mzabasic', 'mzapro'].some((tag) => productTags.value.includes(tag))
);

const isMshCompatible = computed(() =>
  ['mshfree', 'mshpro'].some((tag) => productTags.value.includes(tag))
);

const inStock = computed(() => {
  const { product } = props.offer;
  if (!product) return true;
  return product.availability?.stock > 0;
});

const price = computed(() => getPrice(props.offer.prices, 'DEFAULT', 'ONETIME')?.value);

const wasPrice = computed(() => getPrice(props.offer.prices, 'WAS', 'ONETIME')?.value);

const articleNumbersWithAlternatives = computed(() => {
  const articleNumbers = [props.offer?.product?.articleNumber];
  const properties = props.offer.productAlternatives?.[0]?.properties || [];

  properties.forEach(({ alternatives }) => {
    articleNumbers.push(
      ...alternatives
        .map(({ offer }) => offer?.product?.articleNumber)
        .filter((articleNumber) => articleNumber && !articleNumbers.includes(articleNumber))
    );
  });

  return articleNumbers.filter(Boolean);
});

const hasHighlightFlag = computed(() =>
  articleNumbersWithAlternatives.value.some((articleNumber) =>
    HIGHLIGHTS_OFFER.includes(articleNumber)
  )
);

const discount = computed(() => getDiscount(price.value, wasPrice.value));

const telekomContractLink = computed(() =>
  props.offer.product?.availability?.provider === 'TELEKOM'
    ? getTelekomContractLink(props.offer)
    : ''
);

const contractLinkText = computed(() => getTelekomContractLinkText($route.path));

const isCouponCompatible = computed(() => {
  if (compatibleArticleNumbers.value?.length > 0) {
    return compatibleArticleNumbers.value.includes(props.offer.product.articleNumber);
  }
  if (compatibleProductProviders.value?.length > 0) {
    return compatibleProductProviders.value.includes(props.offer.product.availability.provider);
  }
  if (incompatibleArticleNumbers.value?.length > 0) {
    return !incompatibleArticleNumbers.value.includes(props.offer.product.articleNumber);
  }
  return true;
});

const hasZeroPercentFinancing = computed(() =>
  zeroPercentFinancing.value.includes(props.offer.product.articleNumber)
);

const hasTreueshopFlag = computed(
  () =>
    isValidCoupon.value &&
    getCoupon.value.type === 'coupon' &&
    reductionValue.value &&
    getCoupon.value.typeCode === '02' &&
    isCouponCompatible.value &&
    !props.offer.prices?.some((p) => p.type.toLowerCase() === 'was')
);

const treueShopFlagText = computed(() => {
  if (getCoupon.value.reductionMethodCode === 'RP') return `-${reductionValue.value}% Rabatt`;
  if (['RS', 'RT'].includes(getCoupon.value.reductionMethodCode))
    return `abzüglich ${reductionValue.value} €`;
  return '';
});

const customCard = computed<CardComponentProps | undefined>(() => {
  const cardProps: CardComponentProps = {};

  const squareBadge: SquareBadgeComponentProps[] = [];
  if (rebateDescription.value) {
    squareBadge.push({
      text: 'AKTION',
      theme: 'teal',
    });
  } else if (props.offer.isNew) {
    squareBadge.push({
      text: 'NEU',
      theme: 'teal',
    });
  } else if (discount.value) {
    squareBadge.push({
      text: '%',
      theme: 'teal',
    });
  } else if (hasHighlightFlag.value) {
    squareBadge.push({
      text: 'Bestseller',
      theme: 'teal',
    });
  }

  const showReverse = squareBadge.length === 0;

  // "highlightFlag"
  if (hasTreueshopFlag.value && treueShopFlagText.value) {
    squareBadge.push({
      text: treueShopFlagText.value,
      theme: 'magenta',
    });
  } else if (hasZeroPercentFinancing.value) {
    squareBadge.push({
      text: '0% Finanzierung',
      theme: 'magenta',
      clickBadge: () => {
        openModal({
          component: {
            is: defineAsyncComponent(() => import('~/components/zero-percent-modal-content.vue')),
          },
        });
      },
    });
  }

  if (squareBadge.length > 0) {
    cardProps.squareBadge = squareBadge;
    cardProps.squareBadgeReverse = showReverse;
  }

  merge(cardProps, cloneDeep(props.card));
  return cardProps;
});

const toggleBookmarkNumber = () => {
  toggleBookmark(props.offer?.product?.articleNumber, props.offer?.product?.name, price.value);
};

const clickContractLink = () => {
  tealiumLink({
    wt_link_id: `content.button.zu-den-details.${props.offer?.product?.articleNumber}`,
  });
  window.open(telekomContractLink.value, '_blank');
};

defineExpose({
  price,
  wasPrice,
  rebateDescription,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.offer-tile {
  --max-list-width: 90vw;

  &__bookmark,
  &__add-to-cart {
    position: relative;
    margin-top: -1rem;
  }

  &__bookmark {
    &-icon {
      fill: none;
      stroke: currentColor;
      stroke-width: 1px;
    }

    &--active {
      color: var(--color-primary);
      border-color: var(--color-primary);

      .offer-tile__bookmark-icon {
        fill: currentColor;
      }
    }
  }

  &__delivery,
  &__unavailable {
    flex: 1;
  }

  &__unavailable {
    color: var(--color-error);
  }

  &__contract-link {
    text-align: left;
    @include font-size(16px, 18px);
    white-space: normal;
    padding-block: 3px;
  }

  &__logo-image {
    height: 100%;
    align-self: flex-start;
    object-fit: contain;
    display: inline-block;
  }

  &__brand-placeholder {
    @include font-size(15px, 18px);
  }

  &__image-404 {
    display: inline-block;
    aspect-ratio: 1;
    object-fit: contain;
    background: linear-gradient(135deg, #ececec 0%, #ffffff 100%);
    height: var(--image-height);
  }

  &__svg-icon-wrapper {
    svg {
      height: 2 * $grid-base;
      width: auto;
    }
  }

  :deep(.offer-tile__app-icons) {
    display: flex;
    justify-content: flex-start;
    align-self: flex-end;
    gap: 0.5 * $grid-base;
    height: 2 * $grid-base;
    margin-bottom: var(--grid-gap);
  }
}
</style>
